import {
  TENANT_FEATURE,
  TenantFeature,
} from "@/constants/tenant-feature-constant";
import { EncounterSumaryFormTypeEnum } from "@/services";
import { DateLabelOverrides } from "./initial-f2f-types";

export const INITIAL_F2F_FORM_OPTIONS: {
  name: string;
  value: EncounterSumaryFormTypeEnum;
  feature: TenantFeature;
}[] = [
  {
    name: "Initial F2F",
    value: EncounterSumaryFormTypeEnum.PremierMdInitialF2F,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Readmission F2F",
    value: EncounterSumaryFormTypeEnum.PremierMdReadmissionF2F,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Recertification F2F",
    value: EncounterSumaryFormTypeEnum.PremierMdRecertificationF2F,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "ROC F2F",
    value: EncounterSumaryFormTypeEnum.PremierMdRocF2F,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Follow Up F2F",
    value: EncounterSumaryFormTypeEnum.PremierMdFollowUpF2F,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "30 Day F2F",
    value: EncounterSumaryFormTypeEnum.PremierMd30DayF2F,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Initial Telehealth",
    value: EncounterSumaryFormTypeEnum.PremierMdInitialTelehealth,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Readmission Telehealth",
    value: EncounterSumaryFormTypeEnum.PremierMdReadmissionTelehealth,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Recertification Telehealth",
    value: EncounterSumaryFormTypeEnum.PremierMdRecertificationTelehealth,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "ROC Telehealth",
    value: EncounterSumaryFormTypeEnum.PremierMdRocTelehealth,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "30 Day Telehealth",
    value: EncounterSumaryFormTypeEnum.PremierMd30DayTelehealth,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Cognitive Visit",
    value: EncounterSumaryFormTypeEnum.PremierMdCognitiveVisit,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
  {
    name: "Labs & Comm Note",
    value: EncounterSumaryFormTypeEnum.PremierMdLabsCommNote,
    feature: TENANT_FEATURE.PREMIER_MD_F2F_NOTES,
  },
];

export const INITIAL_F2F_FORM_TYPES: EncounterSumaryFormTypeEnum[] =
  INITIAL_F2F_FORM_OPTIONS.map(({ value }) => value);

// Every telehealth note shares the Initial Telehealth form.
const TELEHEALTH_FORM_TYPES = [
  EncounterSumaryFormTypeEnum.PremierMdInitialTelehealth,
  EncounterSumaryFormTypeEnum.PremierMdReadmissionTelehealth,
  EncounterSumaryFormTypeEnum.PremierMdRecertificationTelehealth,
  EncounterSumaryFormTypeEnum.PremierMdRocTelehealth,
  EncounterSumaryFormTypeEnum.PremierMd30DayTelehealth,
] as const;

const forTelehealth = <T>(
  value: T
): Partial<Record<EncounterSumaryFormTypeEnum, T>> =>
  Object.fromEntries(TELEHEALTH_FORM_TYPES.map((type) => [type, value]));

export const INITIAL_F2F_SECTION_TITLE_OVERRIDES: Partial<
  Record<EncounterSumaryFormTypeEnum, Record<string, string>>
> = forTelehealth({ faceToFace: "Telehealth Encounter" });

// Every Premier MD template labels the dates the same way; add a template here only to rename one.
export const INITIAL_F2F_DATE_LABEL_OVERRIDES: Partial<
  Record<EncounterSumaryFormTypeEnum, DateLabelOverrides>
> = {};

// Only the Cognitive Visit shows the cognitive sections.
const COGNITIVE_SECTION_KEYS = ["miniCog", "cognitiveStatus"];
const HOME_HEALTH_ASSESSMENT_KEYS = [
  "annualWellness",
  "diabeticScreening",
  "smokingScreening",
  "obesityScreening",
];

export const INITIAL_F2F_HIDDEN_SECTION_KEYS: Partial<
  Record<EncounterSumaryFormTypeEnum, string[]>
> = {
  [EncounterSumaryFormTypeEnum.PremierMdInitialF2F]: COGNITIVE_SECTION_KEYS,
  [EncounterSumaryFormTypeEnum.PremierMdReadmissionF2F]: COGNITIVE_SECTION_KEYS,
  [EncounterSumaryFormTypeEnum.PremierMdRecertificationF2F]:
    COGNITIVE_SECTION_KEYS,
  [EncounterSumaryFormTypeEnum.PremierMdRocF2F]: COGNITIVE_SECTION_KEYS,
  [EncounterSumaryFormTypeEnum.PremierMdFollowUpF2F]: COGNITIVE_SECTION_KEYS,
  [EncounterSumaryFormTypeEnum.PremierMd30DayF2F]: COGNITIVE_SECTION_KEYS,
  ...forTelehealth([
    ...COGNITIVE_SECTION_KEYS,
    "reviewOfSystems",
    "vitalSigns",
    "physicalExam",
    ...HOME_HEALTH_ASSESSMENT_KEYS,
  ]),
  [EncounterSumaryFormTypeEnum.PremierMdCognitiveVisit]: [
    "reviewOfSystems",
    "vitalSigns",
    "physicalExam",
    "advanceDirectives",
    "faceToFace",
    "homeHealthOrder",
    "startOfCare",
    ...HOME_HEALTH_ASSESSMENT_KEYS,
  ],
  [EncounterSumaryFormTypeEnum.PremierMdLabsCommNote]: [
    "hospitalization",
    "surgicalHistory",
    "familyHistory",
    "socialHistory",
    "lastPcp",
    "specialist",
    "reviewOfSystems",
    "vitalSigns",
    "physicalExam",
    ...COGNITIVE_SECTION_KEYS,
    "homebound",
    "advanceDirectives",
    "faceToFace",
    ...HOME_HEALTH_ASSESSMENT_KEYS,
  ],
};
